import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";
import Input from "@mui/material/Input";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import ManagedTextField from "utils/edit/ManagedTextField";
import ColourPicker from "utils/elements/ColourPicker";
import Action, { ActionGroup } from "utils/history/Action";
import History from "utils/history/History";
import {
	AddAction,
	DeleteAction,
	ReplaceAction
} from "utils/history/PatchAction";
import { asTitle } from "utils/Utils";

import {
	BlueprintInfo,
	getBlueprintInfo,
	Right
} from "core/blueprint/base/BlueprintService";

import { templatePages3Levels } from "extractblueprint/ExtractBlueprint3Levels";
import { templatePages4Levels } from "extractblueprint/ExtractBlueprint4Levels";
import { ratedCompetencesChart } from "extractblueprint/RatedCompetencesChart";
import { chartBlocks } from "extractblueprint/StandardPieChart";

import {
	getExtractBlueprintContent,
	updateExtractBlueprintContent
} from "extractblueprint/ExtractBlueprintService";

import BlueprintLibraryInfo from "core/blueprint/base/BlueprintLibraryInfo";
import EditActionbar from "core/blueprint/competencelist/EditActionbar";

import {
	createResource,
	deleteResource,
	getResourceInfo,
	updateResourceContent
} from "resource/ResourceService";

import Prompt from "lib/components/prompt/Prompt";
import { defaultColours } from "./DefaultColours";

// TODO: this is just a temporary page until more blueprints are available.
const ExtractlueprintView: React.FC = () => {
	const documentName = "extractBlueprint";

	const blocksPath = "/pages/0/blocks";

	const logoPath = blocksPath + "/1/image";
	const address1Path = blocksPath + "/2/text/text";
	const address2Path = blocksPath + "/3/text/text";

	const titlePath = blocksPath + "/0/text/text";
	const subTitlePath = blocksPath + "/4/text/text";

	const namePath = blocksPath + "/5/text/jsonPath";

	const achievedBlockPath = blocksPath + "/10";
	const achievedOverviewBlock = {
		type: "row",
		x: {
			value: 50.0,
			unit: "POINT"
		},
		y: {
			value: 230.0,
			unit: "POINT"
		},
		width: {
			value: 222.5,
			unit: "POINT"
		},
		height: {
			value: 15.0,
			unit: "POINT"
		},
		absolutePosition: false,
		condition: null,
		loop: null,
		filter: null,
		columnSizes: [25.0, 167.5, 30.0],
		cells: [
			null,
			{
				type: "text",
				x: {
					value: 0.0,
					unit: "POINT"
				},
				y: {
					value: 0.0,
					unit: "POINT"
				},
				width: {
					value: 167.5,
					unit: "POINT"
				},
				height: {
					value: 15.0,
					unit: "POINT"
				},
				absolutePosition: false,
				condition: null,
				loop: null,
				filter: null,
				text: {
					type: "static",
					text: "Aantal behaalde activiteiten"
				},
				font: "liberationsans-regular",
				fontSize: 10.0,
				fontColor: [0.0, 0.0, 0.0],
				halign: "LEFT",
				valign: "MIDDLE"
			},
			{
				type: "text",
				x: {
					value: 0.0,
					unit: "POINT"
				},
				y: {
					value: 0.0,
					unit: "POINT"
				},
				width: {
					value: 30.0,
					unit: "POINT"
				},
				height: {
					value: 15.0,
					unit: "POINT"
				},
				absolutePosition: false,
				condition: null,
				loop: null,
				filter: null,
				text: {
					type: "function",
					function: "count",
					arguments: [
						{
							type: "field",
							jsonPath:
								'$.evaluation.criteria[*].subsections[*].subsections[*].latestProperties..[?(@.achieved == "true")]'
						}
					]
				},
				font: "liberationsans-regular",
				fontSize: 10.0,
				fontColor: [0.0, 0.0, 0.0],
				halign: "RIGHT",
				valign: "MIDDLE"
			}
		]
	};

	const achievedDetailPath = "/pages/1/blocks/0/blocks/1/blocks/1/blocks/1";
	const achievedDetailBlock = {
		type: "text",
		x: {
			value: 40.0,
			unit: "POINT"
		},
		y: {
			value: 15.0,
			unit: "POINT"
		},
		width: {
			value: 455.0,
			unit: "POINT"
		},
		height: {
			value: 0.0,
			unit: "POINT"
		},
		absolutePosition: false,
		condition: {
			type: "function",
			function: "neq",
			arguments: [
				{
					type: "function",
					function: "count",
					arguments: [
						{
							type: "field",
							jsonPath:
								'#.latestProperties..[?(@.achieved == "true")]'
						}
					]
				},
				{
					type: "static",
					text: "0"
				}
			]
		},
		loop: null,
		filter: null,
		text: {
			type: "static",
			text: "De activiteit is behaald"
		},
		font: "liberationsans-bold",
		fontSize: 10.0,
		fontColor: [0.0, 0.0, 1.0],
		halign: "CENTER",
		valign: "TOP"
	};

	const chartTitle = {
		absolutePosition: true,
		condition: null,
		filter: null,
		font: "liberationsans-regular",
		fontColor: [0.0, 0.0, 0.0],
		fontSize: 16.0,
		halign: "LEFT",
		height: {
			unit: "POINT",
			value: 25.0
		},
		loop: null,
		text: {
			text: "Overzicht scores",
			type: "static"
		},
		type: "text",
		valign: "TOP",
		width: {
			unit: "POINT",
			value: 222.5
		},
		x: {
			unit: "POINT",
			value: 50.0
		},
		y: {
			unit: "POINT",
			value: 350.0
		}
	};

	const { t } = useTranslation();

	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const [success, setSuccess] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");

	const [originalLogo, setOriginalLogo] = useState<number | "">();
	const [logoName, setLogoName] = useState("");
	const [logoError, setLogoError] = useState(false);
	const [logos, setLogos] = useState<number[]>([]);

	const [info, setInfo] = useState<BlueprintInfo>();
	const [content, setContent] = useState<unknown>();
	const [actionHistory, setActionHistory] = useState(new History(content));

	useEffect(() => loadExtractBlueprint(), []);
	useEffect(() => {
		const logoId = getLogo();
		if (originalLogo === undefined && content !== undefined) {
			setOriginalLogo(logoId);
		}
		if (logoId !== "") {
			setLogos(logos.concat(logoId));
			getResourceInfo(logoId).then(data => setLogoName(data.name));
		}
	}, [getLogo()]);

	function loadExtractBlueprint() {
		const bpId = Number(id);
		getBlueprintInfo(bpId)
			.then(document => {
				setInfo(document);
			})
			.catch(() => {
				setError(true);
				setErrorMessage(
					t("loadDocumentError", {
						document: documentName
					})
				);
			});
		getExtractBlueprintContent(bpId).then(data => {
			setContent(data);
			setActionHistory(new History(data));
		});
	}

	function undo(): void {
		console.assert(actionHistory.hasPrevious());
		if (actionHistory.hasPrevious()) {
			setContent(actionHistory.undo());
		}
	}

	function redo(): void {
		console.assert(actionHistory.hasNext());
		if (actionHistory.hasNext()) {
			setContent(actionHistory.redo());
		}
	}

	function close(): void {
		// Otherwise we were unable to load in the document.
		if (originalLogo !== undefined) {
			deleteObsoleteLogos(originalLogo);
		}
		navigate("/blueprints");
	}

	function getLogo(): number | "" {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const logoId = getFirstPage()?.blocks[1].image ?? "";
		return logoId !== "" ? Number.parseInt(logoId) : logoId;
	}

	function onChangeLogo(event: ChangeEvent<HTMLInputElement>): void {
		event.preventDefault();

		if (!event.target.files || event.target.files.length === 0) {
			return;
		}

		const f = event.target.files[0];
		if (f.size / 1024 > 50) {
			setLogoError(true);
			return;
		} else {
			setLogoError(false);
		}

		const oldLogoId = getLogo();

		// Already upload the resource.
		const createRequest = {
			name: f.name,
			mediaType: f.type,
			userRights: []
		};
		const data = new FormData();
		data.append("file", f);
		createResource(createRequest)
			.then(location => {
				return Number.parseInt(
					location.substring(location.lastIndexOf("/") + 1)
				);
			})
			.then(id => {
				updateResourceContent(id, data);
				const action = new ReplaceAction(logoPath, oldLogoId, id);
				setContent(actionHistory.apply(action));
			})
			.catch(() => {
				throw new Error("failed to create resource");
			});
	}

	function getFirstPage(): unknown | undefined {
		return getPage(0);
	}

	function getPage(index: number): unknown | undefined {
		if (content === undefined || content === null) {
			return undefined;
		}

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const pages = content.pages;
		if (pages === undefined || pages === null) {
			return undefined;
		}

		if (pages.length > index) {
			return pages[index];
		} else {
			return undefined;
		}
	}

	function getTitle(): string {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return getFirstPage()?.blocks[0].text.text ?? "";
	}

	function changeTitle(value: string): void {
		const action = new ReplaceAction(titlePath, getTitle(), value);
		setContent(actionHistory.apply(action));
	}

	function getSubtitle(): string {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return getFirstPage()?.blocks[4].text.text ?? "";
	}

	function changeSubTitle(value: string): void {
		const action = new ReplaceAction(subTitlePath, getSubtitle(), value);
		setContent(actionHistory.apply(action));
	}

	function getAddress1(): string {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return getFirstPage()?.blocks[2].text.text ?? "";
	}

	function changeAddress1(value: string): void {
		const action = new ReplaceAction(address1Path, getAddress1(), value);
		setContent(actionHistory.apply(action));
	}

	function getAddress2(): string {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return getFirstPage()?.blocks[3].text.text ?? "";
	}

	function changeAddress2(value: string): void {
		const action = new ReplaceAction(address2Path, getAddress2(), value);
		setContent(actionHistory.apply(action));
	}

	function getScoreColors(): number[][] {
		const colorIndex = getAchievedField() ? 3 : 2;
		return (
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			getPage(1)?.blocks[0].blocks[1].blocks[1].blocks[
				colorIndex
			].cells[1].blocks.map((block: any) => block.fillColor) ?? []
		);
	}

	function getColor(index: number): number[] {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return getScoreColors()[index];
	}

	function rgbToHex(color: number[]): string {
		return (
			"#" +
			color
				.map(c => Math.floor(c * 255))
				.map(c => c.toString(16).padStart(2, "0"))
				.join("")
		);
	}

	function hexToRgb(color: string): number[] {
		return color
			.substring(1)
			.split(/(?=(?:..)*$)/)
			.map(p => Number.parseInt(p, 16))
			.map(p => p / 255);
	}

	function changeScoreColor(index: number, color: string): void {
		const newColor = hexToRgb(color);
		const oldColor = getColor(index);
		if (newColor === oldColor) {
			return;
		}

		const actions = [];
		if (getChartType() === "old") {
			const blockIndex = 17 + (getAchievedField() ? 1 : 0);
			const legendColorPaths =
				blocksPath + "/" + blockIndex + "/cells/0/blocks/{i}/fillColor";
			const pieChartColorPath =
				blocksPath + "/" + (blockIndex + 1) + "/series/0/colors";

			actions.push(
				new ReplaceAction(
					pieChartColorPath + "/" + index,
					oldColor,
					newColor
				)
			);
			actions.push(
				new ReplaceAction(
					legendColorPaths.replace("{i}", index.toString()),
					oldColor,
					newColor
				)
			);
		}

		const colorIndex = getAchievedField() ? 3 : 2;
		const overviewColorPaths =
			"/pages/1/blocks/0/blocks/1/blocks/1/blocks/" +
			colorIndex +
			"/cells/1/blocks/{i}/fillColor";

		actions.push(
			new ReplaceAction(
				overviewColorPaths.replace("{i}", index.toString()),
				oldColor,
				newColor
			)
		);

		const group = new ActionGroup(actions);
		setContent(actionHistory.apply(group));
	}

	function changeDiagramColours(): Action[] {
		const newColours = getScoreColors();
		const oldColours = chartBlocks?.[2]?.series?.[0]?.colors ?? [];

		const actions = [];
		for (let index = 0; index < newColours.length; index++) {
			const newColor = newColours[index];
			const oldColor = oldColours[index];
			if (newColor === oldColor) {
				continue;
			}

			const blockIndex = 17 + (getAchievedField() ? 1 : 0);
			const legendColorPaths =
				blocksPath + "/" + blockIndex + "/cells/0/blocks/{i}/fillColor";
			const pieChartColorPath =
				blocksPath + "/" + (blockIndex + 1) + "/series/0/colors";

			actions.push(
				new ReplaceAction(
					pieChartColorPath + "/" + index,
					oldColor,
					newColor
				)
			);
			actions.push(
				new ReplaceAction(
					legendColorPaths.replace("{i}", index.toString()),
					oldColor,
					newColor
				)
			);
		}

		return actions;
	}

	function getNameJson(): string {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return getFirstPage()?.blocks[5].text.jsonPath ?? "";
	}

	function getNameField(): string {
		const nameFieldRegex =
			/[fields|children]\[\?\(@\.name == "([^"]*)"\)\]/g;
		const json = getNameJson();
		let result: RegExpExecArray | null;
		let name = "";

		while ((result = nameFieldRegex.exec(json)) !== null) {
			if (name.length > 0) {
				name += " / ";
			}
			name += result[1];
		}

		return name;
	}

	function changeNameField(value: string): void {
		const newValue = createNameJson(value);
		const action = new ReplaceAction(namePath, getNameJson(), newValue);
		setContent(actionHistory.apply(action));
	}

	function createNameJson(value: string) {
		let json = value
			.split("/")
			.map((part, index) => {
				if (index === 0) {
					return (
						'information.fields[?(@.name == "' + part.trim() + '")]'
					);
				} else {
					return 'children[?(@.name == "' + part.trim() + '")]';
				}
			})
			.join(".");
		json = "$." + json + ".value";
		return json;
	}

	function getAchievedField(): boolean {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const block = getFirstPage()?.blocks[10];
		return (
			block?.type === "row" &&
			block?.cells[1]?.text?.text === "Aantal behaalde activiteiten"
		);
	}

	function changeAchievedField(value: boolean): void {
		if (getAchievedField() && !value) {
			const action = new DeleteAction(
				achievedBlockPath,
				achievedOverviewBlock
			);
			const detailAction = new DeleteAction(
				achievedDetailPath,
				achievedDetailBlock
			);
			const actions = new ActionGroup([action, detailAction]);
			setContent(actionHistory.apply(actions));
		} else if (!getAchievedField() && value) {
			const action = new AddAction(
				achievedBlockPath,
				achievedOverviewBlock
			);
			const detailAction = new AddAction(
				achievedDetailPath,
				achievedDetailBlock
			);
			const actions = new ActionGroup([action, detailAction]);
			setContent(actionHistory.apply(actions));
		}
	}

	function getScoreChart(): boolean {
		const index = 15 + (getAchievedField() ? 1 : 0);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return getFirstPage()?.blocks.length > index;
	}

	function changeScoreChart(value: boolean): void {
		const stopIndex = 15 + (getAchievedField() ? 1 : 0);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const startIndex = getFirstPage()?.blocks.length;

		const actions = [];

		if (!value) {
			for (let index = startIndex - 1; index >= stopIndex; index--) {
				const path = `${blocksPath}/${index}`;
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				const value = getFirstPage()?.blocks[index];
				actions.push(new DeleteAction(path, value));
			}
		} else {
			const titlePath = `${blocksPath}/${stopIndex}`;
			actions.push(new AddAction(titlePath, chartTitle));

			const chartPath = `${blocksPath}/${stopIndex + 1}`;
			actions.push(new AddAction(chartPath, ratedCompetencesChart));
		}

		const group = new ActionGroup(actions);
		setContent(actionHistory.apply(group));
	}

	function getChartType(): string {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const blocks = getFirstPage()?.blocks.length;

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (getFirstPage()?.blocks[blocks - 1].type === "chart") {
			return "old";
		}

		return "new";
	}

	function getDeleteChartActions(): Action[] {
		const stopIndex = 16 + (getAchievedField() ? 1 : 0);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const startIndex = getFirstPage()?.blocks.length;

		const actions = [];
		// Delete current chart.
		for (let index = startIndex - 1; index >= stopIndex; index--) {
			const path = `${blocksPath}/${index}`;
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			const value = getFirstPage()?.blocks[index];
			actions.push(new DeleteAction(path, value));
		}

		return actions;
	}

	function getInsertChartActions(type: string): Action[] {
		const index = 16 + (getAchievedField() ? 1 : 0);

		const actions: Action[] = [];
		if (type === "old") {
			const blocks = chartBlocks.length;
			for (let offset = 0; offset < blocks; offset++) {
				actions.push(
					new AddAction(
						`${blocksPath}/${index + offset}`,
						chartBlocks[offset]
					)
				);
			}

			actions.push(...changeDiagramColours());
		} else {
			actions.push(
				new AddAction(`${blocksPath}/${index}`, ratedCompetencesChart)
			);
		}

		return actions;
	}

	function setChartType(event: ChangeEvent<HTMLInputElement>): void {
		const actions = [
			...getDeleteChartActions(),
			...getInsertChartActions(event.target.value)
		];
		const group = new ActionGroup(actions);
		setContent(actionHistory.apply(group));
	}

	// The Java chart block's own setting (#633): which number a scored
	// competence contributes, its latest score or the mean of all of them.
	// The block sits at the index getInsertChartActions and the save path use.
	function getChartBlockIndex(): number {
		return 16 + (getAchievedField() ? 1 : 0);
	}

	function getChartBlockPath(): string {
		return `${blocksPath}/${getChartBlockIndex()}`;
	}

	function getScoreAggregation(): string {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const block = getFirstPage()?.blocks[getChartBlockIndex()];
		return block?.scoreAggregation ?? "LATEST";
	}

	function setScoreAggregation(event: ChangeEvent<HTMLInputElement>): void {
		const action = new ReplaceAction(
			`${getChartBlockPath()}/scoreAggregation`,
			getScoreAggregation(),
			event.target.value
		);
		setContent(actionHistory.apply(action));
	}

	// Checks validity and shows the correct error message
	function isValid(): boolean {
		if (getLevels() === "") {
			setSuccess(false);
			setError(true);
			setErrorMessage(t("missingRequiredField", { field: "level" }));
			return false;
		}

		return true;
	}

	async function save(): Promise<void> {
		console.assert(actionHistory !== undefined);

		if (!actionHistory.hasPrevious()) {
			return Promise.resolve();
		}

		const actions = actionHistory.getActions();
		const objects = actions.flatMap(action => action.patches());

		const bpId = Number(id);
		return updateExtractBlueprintContent(bpId, objects)
			.then(onSaveSuccess)
			.catch(onSaveFail)
			.finally(() => setLoading(false));
	}

	function onSave(): Promise<void> {
		if (!isValid()) {
			return Promise.resolve();
		}

		if (loading) {
			return Promise.resolve();
		}

		setLoading(true);
		const fn = save();

		deleteObsoleteLogos(getLogo());
		return fn;
	}

	function deleteObsoleteLogos(currentLogo: number | ""): void {
		const unique = new Set(logos);
		unique.forEach(id => {
			if (id !== currentLogo) {
				deleteResource(id);
			}
		});

		if (currentLogo !== "") {
			setLogos([currentLogo]);
		}
		setOriginalLogo(currentLogo);
	}

	function onSaveSuccess(): void {
		setLoading(false);
		setError(false);
		setSuccess(true);
		setSuccessMessage(
			t("saveDocumentSuccess", {
				document: documentName
			})
		);
		loadExtractBlueprint();
	}

	function onSaveFail(): void {
		setLoading(false);
		setError(true);
		setSuccess(false);
		setErrorMessage(
			t("saveDocumentFailure", {
				document: documentName
			})
		);
	}

	function getLevels(): number | "" {
		const index = 12 + (getAchievedField() ? 1 : 0);
		const expression =
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			getFirstPage()?.blocks[index].cells[2].text.arguments[0].jsonPath;
		if (expression === undefined) {
			return "";
		} else if (
			expression ===
			"$.evaluation.criteria[*].subsections[*].subsections[*].subsections[*]"
		) {
			return 4;
		} else if (
			expression ===
			'$.evaluation.criteria[*].subsections[*].subsections[?(@.type == "competence")]'
		) {
			return 3;
		} else {
			return 0;
		}
	}

	function changeLevels(levels: number) {
		const logoId = getLogo();
		const title = getTitle();
		const subtitle = getSubtitle();
		const address1 = getAddress1();
		const address2 = getAddress2();
		const achieved = getAchievedField();

		const defaultName = createNameJson("Naam student");
		const name = getNameJson();

		const baseTemplate =
			levels === 3 ? templatePages3Levels : templatePages4Levels;

		const actions: Action[] = [];
		actions.push(
			new ReplaceAction(
				"/pages",
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				content?.pages ?? "",
				baseTemplate
			)
		);
		actions.push(new ReplaceAction(logoPath, null, logoId));
		actions.push(new ReplaceAction(titlePath, "", title));
		actions.push(new ReplaceAction(subTitlePath, "", subtitle));
		actions.push(new ReplaceAction(address1Path, "", address1));
		actions.push(new ReplaceAction(address2Path, "", address2));
		actions.push(new ReplaceAction(namePath, defaultName, name));

		if (achieved) {
			actions.push(
				new AddAction(achievedBlockPath, achievedOverviewBlock)
			);
			actions.push(
				new AddAction(achievedDetailPath, achievedDetailBlock)
			);
		}

		if (getChartType() === "old") {
			const index = 16 + (getAchievedField() ? 1 : 0);
			const path = `${blocksPath}/${index}`;
			const value = ratedCompetencesChart;
			actions.push(new DeleteAction(path, value));
			actions.push(...getInsertChartActions("old"));
		}

		const blockIndex = 17 + (achieved ? 1 : 0);
		const legendColorPaths =
			blocksPath + "/" + blockIndex + "/cells/0/blocks/{i}/fillColor";
		const pieChartColorPath =
			blocksPath + "/" + (blockIndex + 1) + "/series/0/colors";
		const colorIndex = achieved ? 3 : 2;
		const overviewColorPaths =
			"/pages/1/blocks/0/blocks/1/blocks/1/blocks/" +
			colorIndex +
			"/cells/1/blocks/{i}/fillColor";

		for (let index = 0; index < defaultColours.length; ++index) {
			const defaultColor = defaultColours[index];
			const newColor = getColor(index) ?? defaultColours[index];
			if (getChartType() === "old") {
				actions.push(
					new ReplaceAction(
						pieChartColorPath + "/" + index,
						defaultColor,
						newColor
					)
				);
				actions.push(
					new ReplaceAction(
						legendColorPaths.replace("{i}", index.toString()),
						defaultColor,
						newColor
					)
				);
			}

			actions.push(
				new ReplaceAction(
					overviewColorPaths.replace("{i}", index.toString()),
					defaultColor,
					newColor
				)
			);
		}

		const group = new ActionGroup(actions);
		setContent(actionHistory.apply(group));
	}

	return (
		<>
			{error && (
				<Alert
					id="error-message"
					severity="error"
					onClose={(): void => setError(false)}>
					{errorMessage}
				</Alert>
			)}
			{success && (
				<Alert
					id="success-message"
					severity="success"
					onClose={(): void => setSuccess(false)}>
					{successMessage}
				</Alert>
			)}

			<Typography id="title" variant="h1">
				{info?.name}
			</Typography>

			{info &&
				info.sourceBlueprintId !== null &&
				info.sourceBlueprintVersion !== null && (
					<BlueprintLibraryInfo
						id={info.id}
						enabled={info.rights.includes(Right.ADMIN) ?? false}
						blueprintId={info.sourceBlueprintId}
						blueprintVersion={info.sourceBlueprintVersion}
						onUpdate={loadExtractBlueprint}
					/>
				)}

			<EditActionbar
				undoEnabled={actionHistory.hasPrevious()}
				redoEnabled={actionHistory.hasNext()}
				saveEnabled={actionHistory.hasPrevious()}
				onUndo={undo}
				onRedo={redo}
				onSave={onSave}
				onClose={close}
			/>

			<Grid
				container
				spacing={2}
				direction="column"
				sx={{ marginTop: 2 }}>
				<Grid item container>
					<Grid item xs={4} sm={3} md={2} lg={1}>
						<TextField
							id="levels"
							label={t("level_other")}
							fullWidth
							select
							required
							disabled={!info?.rights.includes(Right.WRITE)}
							value={getLevels()}
							onChange={event =>
								changeLevels(Number(event.target.value))
							}
							variant="standard">
							<MenuItem key={3} value={3}>
								{3}
							</MenuItem>
							<MenuItem key={4} value={4}>
								{4}
							</MenuItem>
						</TextField>
					</Grid>
				</Grid>

				<Grid item container spacing={2} alignItems="center">
					<Grid item xs={8} sm={6} md={4} lg={4}>
						<FormControl fullWidth variant="standard">
							<InputLabel shrink={true}>
								{asTitle(t("logo"))}
							</InputLabel>
							<Input
								id="logo"
								type="file"
								error={logoError}
								inputProps={{ accept: ".jpg,.png" }}
								disabled={getLevels() === ""}
								onChange={onChangeLogo}
							/>
							<FormHelperText>
								{t("fileMaxSize", { size: "50kb" })}
							</FormHelperText>
						</FormControl>
					</Grid>
					<Grid item xs={4} sm={3} md={2} lg={2}>
						{logoName}
					</Grid>
				</Grid>
				<Grid item container spacing={2}>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<ManagedTextField
							id="title"
							label={t("title")}
							fullWidth
							disabled={
								!info?.rights.includes(Right.WRITE) ||
								getLevels() === ""
							}
							value={getTitle()}
							onUpdate={changeTitle}
							variant="standard"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<ManagedTextField
							id="subtitle"
							label={t("subtitle")}
							fullWidth
							disabled={
								!info?.rights.includes(Right.WRITE) ||
								getLevels() === ""
							}
							value={getSubtitle()}
							onUpdate={changeSubTitle}
							variant="standard"
						/>
					</Grid>
				</Grid>
				<Grid item container spacing={2}>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<ManagedTextField
							id="address1"
							label={t("address1")}
							fullWidth
							disabled={
								!info?.rights.includes(Right.WRITE) ||
								getLevels() === ""
							}
							value={getAddress1()}
							onUpdate={changeAddress1}
							variant="standard"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<ManagedTextField
							id="address2"
							label={t("address2")}
							fullWidth
							disabled={
								!info?.rights.includes(Right.WRITE) ||
								getLevels() === ""
							}
							value={getAddress2()}
							onUpdate={changeAddress2}
							variant="standard"
						/>
					</Grid>
				</Grid>
				<Grid item container spacing={2}>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<ManagedTextField
							id="name"
							label={t("nameField")}
							fullWidth
							disabled={
								!info?.rights.includes(Right.WRITE) ||
								getLevels() === ""
							}
							value={getNameField()}
							onUpdate={changeNameField}
							variant="standard"
						/>
					</Grid>
				</Grid>
				<Grid item container spacing={2}>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<FormGroup>
							<FormControlLabel
								control={
									<Checkbox
										disabled={
											!info?.rights.includes(
												Right.WRITE
											) || getLevels() === ""
										}
										checked={getAchievedField()}
										onChange={e =>
											changeAchievedField(
												e.target.checked
											)
										}
									/>
								}
								id="showAchieved"
								label={t("showAchieved")}
							/>
						</FormGroup>
					</Grid>
				</Grid>
				<Grid item container spacing={2}>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<FormGroup>
							<FormControlLabel
								control={
									<Checkbox
										disabled={
											!info?.rights.includes(
												Right.WRITE
											) || getLevels() === ""
										}
										checked={getScoreChart()}
										onChange={e =>
											changeScoreChart(e.target.checked)
										}
									/>
								}
								id="showScoreChart"
								label={t("showScoreChart")}
							/>
						</FormGroup>
					</Grid>
				</Grid>
				{getScoreChart() && (
					<Grid item container spacing={2}>
						<Grid item sm={12} md={8} lg={6}>
							<FormGroup>
								<FormLabel>{t("scoreChartVariant")}</FormLabel>
								<RadioGroup
									value={getChartType()}
									onChange={setChartType}>
									<FormControlLabel
										value="new"
										control={<Radio />}
										label={t("newChartVariant")}
									/>
									<FormControlLabel
										value="old"
										control={<Radio />}
										label={t("oldChartVariant")}
									/>
								</RadioGroup>
							</FormGroup>
						</Grid>
					</Grid>
				)}
				{getScoreChart() && getChartType() === "new" && (
					<Grid item container spacing={2}>
						<Grid item sm={12} md={8} lg={6}>
							<FormGroup>
								<FormLabel>{asTitle(t("scoreAggregation"))}</FormLabel>
								<RadioGroup
									value={getScoreAggregation()}
									onChange={setScoreAggregation}>
									<FormControlLabel
										value="LATEST"
										control={<Radio />}
										label={t("scoreAggregationLatest")}
									/>
									<FormControlLabel
										value="AVERAGE"
										control={<Radio />}
										label={t("scoreAggregationAverage")}
									/>
								</RadioGroup>
								<FormHelperText>
									{t("scoreAggregationInfo")}
								</FormHelperText>
							</FormGroup>
						</Grid>
					</Grid>
				)}
				{getScoreChart() && (
					<Grid item container direction="column">
						<Grid item>
							<Typography>
								{asTitle(t("colour_other"))}
							</Typography>
							<Typography variant="caption">
								{t("colour_details")}
							</Typography>
						</Grid>
						<Grid item container>
							<Grid item>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>
												{asTitle(t("score"))}
											</TableCell>
											<TableCell>
												{asTitle(t("colour"))}
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{getScoreColors().map((row, index) => (
											<TableRow key={index}>
												<TableCell
													style={{
														width: 40
													}}
													component="th"
													scope="row">
													<Typography>
														{index + 1}
													</Typography>
												</TableCell>
												<TableCell
													style={{
														width: 40
													}}>
													<ColourPicker
														value={rgbToHex(row)}
														onChange={value =>
															changeScoreColor(
																index,
																value
															)
														}
													/>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Grid>
						</Grid>
					</Grid>
				)}
			</Grid>

			<Prompt
				when={actionHistory.hasPrevious()}
				message={t("exitDocumentMessage")}
			/>
		</>
	);
};

export default ExtractlueprintView;
